// ============================================================================
// ResumeCaseModal — case-level resume action
// ============================================================================
// Resume just clears the hold flag with a mandatory remark for audit.

import React, { useCallback, useEffect, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { useResumeCase } from 'hooks/api/useCaseClosure';

export interface ResumeCaseModalProps {
  isOpen: boolean;
  caseId: string;
  caseCode: string;
  onClose: () => void;
  onResumed: () => void;
}

const ResumeCaseModal: React.FC<ResumeCaseModalProps> = ({
  isOpen, caseId, caseCode, onClose, onResumed,
}) => {
  const { resume, loading: saving } = useResumeCase();
  const [remarks, setRemarks] = useState('');
  const [error, setError] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setRemarks('');
    setError('');
  }, [isOpen]);

  const handleSave = useCallback(() => {
    if (!remarks.trim()) { setError('Remarks are required'); return; }
    setError('');
    setConfirmOpen(true);
  }, [remarks]);

  const handleConfirm = useCallback(async () => {
    setConfirmOpen(false);
    try {
      await resume(caseId, { remarks: remarks.trim() });
      notificationService.toast.success(`Case ${caseCode} resumed.`);
      onResumed();
      onClose();
    } catch (err) {
      notificationService.toast.error(
        err instanceof Error ? err.message : 'Failed to resume case.',
      );
    }
  }, [resume, caseId, caseCode, remarks, onResumed, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm">
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Resume Case — {caseCode}</h2>
          <button className="modal-close" onClick={onClose} disabled={saving}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div className="form-group" data-field="remarks">
            <label className="form-label">Remarks <span className="text-danger">*</span></label>
            <textarea
              className={`form-textarea${error ? ' form-field-error' : ''}`}
              rows={3}
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              placeholder="Why is the case being resumed?"
            />
            {error && <div className="form-error-message">{error}</div>}
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Resuming…' : 'Resume Case'}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title="Confirm Resume"
        message={`Resume case ${caseCode}? Workflow transitions will be permitted again.`}
        confirmLabel="Resume"
        cancelLabel="Cancel"
        variant="default"
        loading={saving}
        loadingMessage="Resuming…"
        onConfirm={handleConfirm}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default ResumeCaseModal;
